import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { fetchDelayedPayments, DelayedPaymentRow, peekCache } from '../services/api';
import { useRealtime } from '../hooks/useRealtime'; // [REALTIME] 2026-06-23
import { useIsMobile } from '../hooks/useIsMobile'; // [MOBILE 2026-07-07]
import SearchBar from '../components/SearchBar';
import DataTable from '../components/DataTable';
import { useAuth } from '../contexts/AuthContext'; // [COL-RESIZE-ALL 2026-09-29] keys the remembered column widths
import { TableSkeleton } from '../components/Skeleton';
import { LoadFailed, EmptyState } from '../components/LoadState'; // [LOAD-STATES 2026-09-29]
import { Column, SortConfig } from '../types';

// [STICKY-HEAD 2026-06-26] Same freeze pattern as PaymentsPage: fill the scroll
// card exactly and don't scroll HERE — the heading/search toolbar stays fixed and
// only the table area (below) scrolls. (Needs AppLayout's PageCard $flush on the
// /delayed-payments route so the card itself doesn't also scroll.)
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  background: ${(p) => p.theme.surface};

  /* [PHONE-LAYOUT 2026-10-23, user] phones: NOTHING is fixed at the top — the heading, the search and the table are ONE
     scroll ("in the phone view nothing will be fixed on the top side otherwise how will i see the data for the below").
     The table only swipes sideways (TableScroll below). */
  @media (max-width: 768px) {
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
  }
`;

// The FIXED top block — the Delayed Payments heading + search toolbar. Sits OUTSIDE
// the scrolling table area (flex-shrink:0) so it never scrolls.
const FrozenTop = styled.div`
  flex-shrink: 0;
  background: ${(p) => p.theme.surface};
`;

// The scrolling table region — owns the vertical scroll. The DataTable's sticky
// <thead> pins to the top of THIS area (via parentScroll), so the column header
// stays just under the frozen heading while rows scroll. padding-bottom clears the
// floating dock at the very end of the scroll so the last row never hides behind it.
const TableScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: scroll; /* [SCROLLBAR 2026-07-08] always show the vertical scroller on desktop */
  padding-bottom: 88px;

  /* [PHONE-LAYOUT 2026-10-23, user] phones: as tall as its rows (no box of its own to scroll up / down in) — it only
     swipes sideways; the page scrolls (Container). The 88 px end pad still lifts the last row above the dock. */
  @media (max-width: 768px) {
    flex: none;
    min-height: 0;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    overscroll-behavior-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.04) 100%)'
    : 'linear-gradient(180deg, rgba(241,245,249,0.85) 0%, rgba(229,232,231,0.72) 100%)')};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch) and the blur did nothing over the solid card. */
  border-bottom: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.10)')};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)')};
  flex-wrap: wrap;
  gap: 10px;

  /* Phones: stack title / search / filters vertically (each full-width). The
     Filters row already wraps. Desktop layout is unchanged. */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    padding: 10px 12px;
    /* [PHONE-LAYOUT 2026-10-23] each line at its own height: the inline search's flex: 1 (made to fill a ROW) squeezed
       it to 22 px tall in this column */
    & > * { flex: none; }
  }
`;

const PageTitle = styled.h1`
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  margin: 0;
`;

// [MOBILE 2026-07-07] Phones only: title + record count share one row (count
// pushed to the right), with the search field on its own full-width row below.
// Desktop keeps title | search | count inline (see the render branch).
const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`;

const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const RecordCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;
`;


const columns: Column[] = [
  { key: 'website', label: 'Website', sortable: true },
  { key: 'email', label: 'Email ID', sortable: true },
  { key: 'pm', label: 'PM', sortable: true },
  { key: 'brand', label: 'Brand', sortable: true },
  { key: 'amount', label: 'Amount', align: 'center', sortable: true },
  { key: 'billing_date', label: 'Billing Date', align: 'center', sortable: true },
  { key: 'status', label: 'Status', align: 'center', sortable: true },
  { key: 'total_payment_received', label: 'Total Payment Received', align: 'center', sortable: true },
  { key: 'recurring_retention', label: 'Recurring Retention', align: 'center', sortable: true },
  { key: 'retention_status', label: 'Status', align: 'center', sortable: true },
];

// Chronological key for a month label so sorting is by real date, not
// alphabetical. Handles both "May 2026" (sheet format) and ISO "2026-05".
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
function monthSortKey(m: string | null): number {
  if (!m) return -1;
  const s = m.trim();
  const iso = s.match(/^(\d{4})-(\d{1,2})/);
  if (iso) return parseInt(iso[1], 10) * 12 + (parseInt(iso[2], 10) - 1);
  const parts = s.toLowerCase().split(/\s+/);
  if (parts.length === 2) {
    const mi = MONTHS.indexOf(parts[0].slice(0, 3));
    const y = parseInt(parts[1], 10);
    if (mi >= 0 && !isNaN(y)) return y * 12 + mi;
  }
  return -1;
}

const DelayedPaymentsPage: React.FC = () => {
  const { user } = useAuth(); // [COL-RESIZE-ALL 2026-09-29]
  // [TAB-FAST 2026-07-07] Seed from the SWR GET cache (idle-prefetch/prior-visit warmed) so a
  // warmed revisit paints rows immediately with no skeleton; a cold visit still shows it. The
  // mount effect below still fetches and revalidates. A miss is a safe no-op (empty + skeleton).
  const [rows, setRows] = useState<DelayedPaymentRow[]>(() => peekCache<{ rows: DelayedPaymentRow[] }>('/delayed-payments')?.rows ?? []);
  const [loading, setLoading] = useState<boolean>(() => !peekCache<{ rows: DelayedPaymentRow[] }>('/delayed-payments'));
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortConfig>({ sortBy: '', sortOrder: 'asc' });
  const isMobile = useIsMobile(); // [MOBILE 2026-07-07] drives the header reflow + expanded cards
  // [LOAD-STATES 2026-09-29, user] The first load failed: show the countdown card, not "No delayed
  // payments found". The quiet refreshes below never set it (they keep the rows already shown).
  const [loadFailed, setLoadFailed] = useState(false);

  // Pull the latest rows from the server cache (cheap — no sheet round-trip).
  const load = useCallback(async () => {
    try {
      const res = await fetchDelayedPayments();
      setRows(res.rows);
      setLoadFailed(false); // [LOAD-STATES 2026-09-29]
    } catch (err) {
      console.error('Failed to load delayed payments:', err);
    }
  }, []);

  // Initial load, then keep an open page current without any clicks: poll the
  // cache every 30s and refetch whenever the tab regains focus. Paired with the
  // server's 60s background sheet pull, sheet edits surface here on their own.
  useEffect(() => {
    let alive = true;
    fetchDelayedPayments()
      .then((res) => { if (alive) { setRows(res.rows); setLoadFailed(false); } })
      .catch((err) => { console.error('Failed to load delayed payments:', err); if (alive) setLoadFailed(true); })
      .finally(() => { if (alive) setLoading(false); });

    // Skip while hidden — the focus listener below reloads the moment the tab returns.
    const id = window.setInterval(() => { if (document.hidden) return; load(); }, 30 * 1000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      alive = false;
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  // [REALTIME] 2026-06-23 — silently refresh when delayed-payment inputs change.
  useRealtime(['delayed'], () => { load(); });

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    let out = rows.filter(r => {
      if (s) {
        const hay = [r.website, r.email].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
    // An explicit column sort wins; otherwise, while searching, default to
    // newest month first so the matches read top-down from the latest month.
    const effSortBy = sort.sortBy || (s ? 'month' : '');
    const effOrder = sort.sortBy ? sort.sortOrder : 'desc';
    if (effSortBy) {
      const k = effSortBy as keyof DelayedPaymentRow;
      const dir = effOrder === 'asc' ? 1 : -1;
      out = [...out].sort((a, b) => {
        if (k === 'month') return (monthSortKey(a.month) - monthSortKey(b.month)) * dir;
        const av = (a[k] ?? '').toString();
        const bv = (b[k] ?? '').toString();
        const an = parseFloat(av.replace(/[$,\s]/g, ''));
        const bn = parseFloat(bv.replace(/[$,\s]/g, ''));
        if (!isNaN(an) && !isNaN(bn)) return (an - bn) * dir;
        return av.localeCompare(bv) * dir;
      });
    }
    return out;
  }, [rows, search, sort]);

  const handleSort = (key: string) => {
    setSort((prev) => prev.sortBy === key
      ? { sortBy: key, sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc' }
      : { sortBy: key, sortOrder: 'asc' });
  };

  // [LOAD-STATES 2026-09-29, user] Failed with nothing on screen → the countdown card.
  const failedView = loadFailed && rows.length === 0;
  const countText = failedView ? 'Not loaded' : `${filtered.length.toLocaleString()} records fetched`;

  return (
    <Container>
      <FrozenTop>
        <Toolbar>
          {isMobile ? (
            // Phones: [ Delayed Payments ......... N records fetched ] then search below.
            <>
              <TitleRow>
                <PageTitle>Delayed Payments</PageTitle>
                <RecordCount>{countText}</RecordCount>
              </TitleRow>
              <SearchBar inline liquid /* [LIQUID-GLASS 2026-11-02] */ value={search} onChange={(v) => setSearch(v)} />
            </>
          ) : (
            // Desktop: title | search (fills the gap) | record count, all inline.
            <>
              <PageTitle>Delayed Payments</PageTitle>
              <SearchBar inline liquid /* [LIQUID-GLASS 2026-11-02] */ value={search} onChange={(v) => setSearch(v)} />
              <Filters>
                <RecordCount>{countText}</RecordCount>
              </Filters>
            </>
          )}
        </Toolbar>
      </FrozenTop>
      <TableScroll>
        {loading ? (
          <TableSkeleton columns={columns} />
        ) : failedView ? (
          <LoadFailed what="delayed payments" onRetry={load} />
        ) : filtered.length === 0 ? (
          // [LOAD-STATES 2026-09-29, user] Say what is empty and offer the fix.
          search.trim()
            ? <EmptyState icon="search" title={`No delayed payments match “${search.trim()}”`} hint="Check the spelling, or clear the search to see every delayed payment." action={{ label: 'Clear search', onClick: () => setSearch('') }} />
            : <EmptyState title="No delayed payments right now" hint="Clients who are late to pay show up here." />
        ) : (
          <DataTable
            parentScroll
            columns={columns}
            data={filtered}
            sort={sort}
            onSort={handleSort}
            // [MOBILE 2026-07-07] Keep the SAME desktop table on phones (no card
            // view) — it scrolls horizontally inside the page's TableScroll so the
            // full grid reads exactly like the website.
            disableMobileCards
            // [COL-RESIZE-ALL 2026-09-29, user] Drag a header's edge to resize (same as Payments).
            resizeStorageKey={`dt-delayed-payments-colw:${user?.id ?? 'anon'}`}
            highlight={search} // [SEARCH-HIGHLIGHT 2026-09-29]
          />
        )}
      </TableScroll>
    </Container>
  );
};

export default DelayedPaymentsPage;
